import * as BaseTooltip from "@radix-ui/react-tooltip";
import { cx } from "class-variance-authority";
import { Ref } from "react";

export interface TooltipProps {
  content: React.ReactNode;
  align?: BaseTooltip.TooltipContentProps["align"];
  side?: BaseTooltip.TooltipContentProps["side"];
  /**
   * Use when the tooltip trigger is already interactive.
   */
  asChild?: boolean;
  isDisabled?: boolean;
  children: React.ReactNode;
  /**
   * Only applies when `asChild !== true`.
   */
  triggerRef?: Ref<HTMLButtonElement>;
  triggerTestId?: string;
  contentWrapperRef?: Ref<HTMLDivElement>;
  contentWrapperTestId?: string;
  contentAlignment?: "text-center" | "text-left" | "text-right";
}
export const Tooltip = ({
  align = "center",
  side = "top",
  ...props
}: TooltipProps) => {
  if (!props.content || props.isDisabled) return <>{props.children}</>;
  return (
    <BaseTooltip.Root delayDuration={0}>
      <BaseTooltip.Trigger
        data-testid={props.triggerTestId}
        asChild={props.asChild}
        // So that clicking tooltip does not submit <form>
        // https://stackoverflow.com/a/10836076
        type={props.asChild ? undefined : "button"}
        className={cx(!props.asChild ? "outline-offset-2" : "")}
        ref={props.triggerRef}
      >
        {props.children}
      </BaseTooltip.Trigger>
      {/* So it appears over other elements such as sticky table headers */}
      <BaseTooltip.Portal>
        <BaseTooltip.Content
          data-testid={props.contentWrapperTestId}
          align={align}
          side={side}
          ref={props.contentWrapperRef}
          className={cx(
            "max-w-xs",
            "pointer-events-none select-none rounded bg-charcoal shadow-md",
            "text-sm text-white",
            "p-3",
            // So it appears over other elements such as sticky table headers
            // 9999 goes over modals
            "z-[9999]",
            props.contentAlignment ?? "text-center",
          )}
        >
          {props.content}
          <BaseTooltip.Arrow
            className={cx("fill-current")}
            offset={5}
            width={11}
            height={5}
            alignmentBaseline="central"
          />
        </BaseTooltip.Content>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  );
};

/**
 * Tooltip from RadixUI must be wrapped in a provider
 */
export const TooltipProvider = BaseTooltip.Provider;
